<script setup lang="ts">
import { searchItemSelected } from "@/analytics/events/search";
import { trackEvent } from "@/analytics/track";
import type { ResolvedCard } from "@/model/card";
import type { SearchResultKey } from "@/model/search";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";

import ListItemStickySearch from "./ListItemStickySearch.vue";

const emit = defineEmits<{ select: [item: SearchResultKey] }>();

const props = defineProps<{
  results: ResolvedCard[];
  selected?: SearchResultKey;
  boardId: string;
}>();

function isCurrentBoard(boardId: string) {
  return useBoardStore().boardId() === boardId;
}

function isSelected(item: ResolvedCard) {
  return item.id === props.selected?.id && item.team?.id === props.selected?.teamId;
}

function select(item: ResolvedCard) {
  const otherBoardId = isCurrentBoard(props.boardId) ? undefined : props.boardId;
  trackItemSelected(item, otherBoardId);
  emit("select", { id: item.id, boardId: otherBoardId, teamId: item.team?.id });
}

// used for analytics
function trackItemSelected(_: ResolvedCard, boardId?: string) {
  const currentBoardType = useBoardStore().currentBoard().type;
  const targetBoardType = boardId ? useBoardsStore().boards[boardId].type : undefined;

  trackEvent(searchItemSelected(currentBoardType, targetBoardType));
}
</script>

<template>
  <ListItemStickySearch
    v-for="item in results"
    :id="`result-${item.id}`"
    :key="item.id"
    :data-id="item.id"
    class="search-result-list base-search-item result-card"
    :class="{ selected: isSelected(item) }"
    :item
    :board-id="boardId"
    @click.stop="select(item)"
  />
</template>

<style lang="scss">
// Can't make this scoped because the scoping doesn't seem to work
// with ListItemStickySearch (ListItem's inheritAttr: false might be the culprit)
@use "@/styles/font";
@use "@/styles/variables/colors";

.search-result-list.base-search-item {
  &.current,
  &:focus-visible {
    background-color: colors.$background-grey-light;
    cursor: pointer;

    .high-contrast-mode & {
      // In high-contrast mode, the background color alone has too low contrast
      outline: 2px solid colors.$outline;
      outline-offset: -2px;
    }
  }

  // Emphasize the text of the selected item
  // (ie. the one marked as found by SearchBase)
  &.selected {
    .title {
      font-weight: font.$weight-bold;
    }
  }
}
</style>
